/**
 * Exact-target persistence for the legacy Demos walkthroughs.
 *
 * - Targets are resolved BEFORE any write: the Demos instance, its demo space,
 *   exactly one scenario root, and exactly the scenario's named descendants.
 *   Duplicates, wrong instance/project, or descendants the script does not own
 *   refuse rather than guess.
 * - Every write inspects the saved row; completion only goes through the
 *   canonical `complete_task_if_ready` gate.
 * - A pre-walkthrough snapshot of the exact task rows, their review records and
 *   their dependency links is captured before the first write, kept in local
 *   storage as durable restoration material, and restored exactly on exit.
 * - Every review row the engine writes is a SYNTHETIC persona row
 *   (`demo-<slot>@walkthrough.workitect.invalid`, never a real user/email,
 *   no token). Interactive checkpoints leave the persona's current-round row
 *   pending; the presenter decides it through the shared review controls,
 *   which route confirmed Demos sessions to `demo_walkthrough_decide_approval`
 *   (unapplied candidate). The engine never overwrites that decision.
 * - Scenarios whose tasks already carry any review record refuse before any
 *   mutation, so restore never has to recreate ids/tokens/dispatch relations.
 */
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import type { DemoScenario } from '@/data/demoScenarios';
import type { DemoAck } from './demoBus';
import { currentRound, demoIso, type DemoApprovalRow, type DemoWorld } from './demoWorld';
import { demoPersonaEmail, syntheticPersonaSlot } from './demoDecisionSession';

export const DEMOS_INSTANCE_ID = '87273678-64d6-41b4-961c-7357d2631c75';

export interface ScenarioTargets {
  instanceId: string;
  spaceId: string;
  projectId: string;
  rootId: string;
  taskIds: Record<string, string>;
}

export type ResolveResult = { ok: true; targets: ScenarioTargets } | { ok: false; reason: string; missing?: boolean };

export async function resolveScenarioTargets(
  scenario: DemoScenario,
  spaceId: string | null | undefined,
  instanceId: string | null | undefined,
): Promise<ResolveResult> {
  if (instanceId !== DEMOS_INSTANCE_ID) return { ok: false, reason: 'Walkthroughs only run in the Demos workspace.' };
  if (!spaceId) return { ok: false, reason: 'The Demos space is not available.' };

  const { data: space, error: spaceErr } = await supabase.from('spaces').select('id, instance_id').eq('id', spaceId).maybeSingle();
  if (spaceErr || !space) return { ok: false, reason: 'Could not confirm the Demos space.' };
  if (space.instance_id !== DEMOS_INSTANCE_ID) return { ok: false, reason: 'The selected space is not in the Demos workspace.' };

  const rootName = scenario.tasks.find(t => !t.parentName)?.name;
  if (!rootName) return { ok: false, reason: 'Scenario has no project task.' };

  const { data: roots, error: rootErr } = await supabase
    .from('tasks').select('id, project_id')
    .eq('space_id', spaceId).eq('name', rootName).is('parent_id', null).is('deleted_at', null);
  if (rootErr) return { ok: false, reason: `Could not read the scenario project: ${rootErr.message}` };
  if (!roots || roots.length === 0) return { ok: false, missing: true, reason: `"${rootName}" does not exist in the Demos space yet.` };
  if (roots.length !== 1) return { ok: false, reason: `Expected one "${rootName}" project in the Demos space, found ${roots.length}.` };
  const root = roots[0];

  const names = scenario.tasks.filter(t => t.parentName).map(t => t.name);
  const { data: rows, error: rowsErr } = await supabase
    .from('tasks').select('id, name, project_id, space_id, parent_id')
    .eq('root_project_id', root.id).is('deleted_at', null);
  if (rowsErr) return { ok: false, reason: `Could not read scenario tasks: ${rowsErr.message}` };

  const owned = new Set(names);
  const unowned = (rows ?? []).filter(r => r.id !== root.id && !owned.has(r.name));
  if (unowned.length) {
    return { ok: false, reason: `"${rootName}" contains ${unowned.length} task(s) the walkthrough does not own (e.g. "${unowned[0].name}"). Nothing was changed.` };
  }

  const taskIds: Record<string, string> = { [rootName]: root.id };
  for (const name of names) {
    const matches = (rows ?? []).filter(r => r.name === name);
    if (matches.length !== 1) return { ok: false, reason: `Expected one "${name}" task, found ${matches.length}.` };
    const m = matches[0];
    if (m.project_id !== root.project_id || m.space_id !== spaceId) return { ok: false, reason: `"${name}" is outside the scenario project.` };
    taskIds[name] = m.id;
  }
  // Parent links must match the script exactly.
  for (const t of scenario.tasks.filter(x => x.parentName)) {
    const row = (rows ?? []).find(r => r.id === taskIds[t.name]);
    if (row?.parent_id !== taskIds[t.parentName!]) return { ok: false, reason: `"${t.name}" is not under "${t.parentName}".` };
  }
  return { ok: true, targets: { instanceId: DEMOS_INSTANCE_ID, spaceId, projectId: root.project_id, rootId: root.id, taskIds } };
}

/** Children before parents, siblings in script order. */
export function persistenceOrder(scenario: DemoScenario): string[] {
  const out: string[] = [];
  const visit = (name: string) => {
    for (const child of scenario.tasks.filter(t => t.parentName === name)) visit(child.name);
    out.push(name);
  };
  for (const root of scenario.tasks.filter(t => !t.parentName)) visit(root.name);
  return out;
}


type DemoItem = { id: string; text: string; completed: boolean };

/**
 * Map the walkthrough's scripted revision items ("<task>:revision:<n>") onto
 * the ids the shared review panel derives from saved rejected reviews
 * ("approval-change-<reviewId>-<i>", one per changes_needed line), keeping
 * each scripted item's completion. The nth scripted revision belongs to the
 * nth rejected review in sequence order. Order matches the shared merge:
 * non-revision items first, then revision items. Refuses on any mismatch so a
 * reload can never add an unchecked duplicate.
 */
export function canonicalRevisionItems(
  items: DemoItem[],
  rejected: Array<{ id: string; changes_needed: string[] | null }>,
): { ok: true; items: DemoItem[] } | { ok: false; reason: string } {
  const plain = items.filter(i => !i.text.startsWith('[Revision]'));
  const scripted = items.filter(i => i.text.startsWith('[Revision]'));
  if (scripted.length !== rejected.length) {
    return { ok: false, reason: `revision items (${scripted.length}) do not match rejected reviews (${rejected.length}).` };
  }
  const out: DemoItem[] = [];
  scripted.forEach((item, n) => {
    const rec = rejected[n];
    const lines = (rec.changes_needed ?? []).filter(l => typeof l === 'string');
    lines.forEach((line, i) => out.push({ id: `approval-change-${rec.id}-${i}`, text: `[Revision] ${line}`, completed: item.completed }));
  });
  if (rejected.some(r => !(r.changes_needed ?? []).length)) return { ok: false, reason: 'a rejected review has no revision lines.' };
  return { ok: true, items: [...plain, ...out] };
}

interface RowRef { id: string; status: string; live: boolean }

/**
 * Per-walkthrough persistence state: which database review row backs each
 * scripted review (by task + sequence). Cleared on every baseline reset.
 */
export class DemoPersistSession {
  private rows = new Map<string, RowRef>();
  private cleared = new Set<string>();
  constructor(readonly scenario: DemoScenario, readonly targets: ScenarioTargets, readonly presenterId: string) {}

  reset() { this.rows.clear(); this.cleared.clear(); }

  liveRowFor(taskName: string, seq: number): RowRef | undefined {
    const r = this.rows.get(`${taskName}#${seq}`);
    return r?.live ? r : undefined;
  }

  async persistApprovals(taskName: string, rows: DemoApprovalRow[], baseMs: number, liveSeq: number | null): Promise<DemoAck> {
    const taskId = this.targets.taskIds[taskName];
    if (!this.cleared.has(taskName)) {
      // First write after a reset: only synthetic walkthrough rows may exist here.
      const ack = await deleteSyntheticApprovals([taskId]);
      if (!ack.ok) return ack;
      this.cleared.add(taskName);
    }
    for (const row of rows) {
      const key = `${taskName}#${row.seq}`;
      const existing = this.rows.get(key);
      const wantLive = row.live || row.seq === liveSeq;
      if (existing?.live) {
        // Presenter-owned decision: verify only, never overwrite.
        const { data, error } = await supabase.from('task_approvals').select('status').eq('id', existing.id).maybeSingle();
        if (error || !data) return { ok: false, reason: `Review record missing: ${error?.message ?? 'no row'}` };
        if (data.status !== row.status) return { ok: false, reason: `Your decision on "${taskName}" is ${data.status}; this chapter needs ${row.status}.` };
        continue;
      }
      if (existing && existing.status === row.status && !wantLive) continue;
      if (existing) {
        const { error } = await supabase.from('task_approvals').delete().eq('id', existing.id).eq('task_id', taskId).eq('project_id', DEMOS_PROJECT_ID);
        if (error) return { ok: false, reason: `Could not update a review record: ${error.message}` };
        this.rows.delete(key);
      }
      const createdAt = demoIso(baseMs, row.seq);
      const newId = crypto.randomUUID();
      if (!recordOwnedReviewId(newId)) return { ok: false, reason: 'Could not record the walkthrough review in this browser; nothing was created.' };
      const insert = {
        id: newId, task_id: taskId, project_id: this.targets.projectId, approver_type: 'email',
        approver_name: row.approverName, approver_email: demoPersonaEmail(row.approverSlot),
        status: wantLive ? 'pending' : row.status, comments: wantLive ? null : row.comments,
        changes_needed: !wantLive && row.status === 'rejected' && row.comments ? [row.comments] : null,
        responded_at: wantLive || row.status === 'pending' ? null : createdAt, created_at: createdAt, scope: 'task',
      };
      const { data, error } = await supabase.from('task_approvals').insert(insert as never).select('id, status').maybeSingle();
      if (error || !data) return { ok: false, reason: `Review record not saved: ${error?.message ?? 'no row returned'}` };
      if (data.id !== newId) return { ok: false, reason: 'Review record saved with an unexpected id.' };
      const expected = wantLive ? 'pending' : row.status;
      if (data.status !== expected) return { ok: false, reason: `Review saved as ${data.status}, expected ${expected}.` };
      this.rows.set(key, { id: data.id, status: data.status, live: wantLive });
    }
    return { ok: true };
  }

  async persistWorld(world: DemoWorld, names: string[], baseMs: number, isCurrent: () => boolean, live?: { task: string; seq: number } | null): Promise<DemoAck> {
    const wanted = new Set(names);
    if (live) wanted.add(live.task);
    for (const name of persistenceOrder(this.scenario)) {
      if (!wanted.has(name)) continue;
      if (!isCurrent()) return { ok: false, reason: 'superseded' };
      const id = this.targets.taskIds[name];
      const state = world.tasks[name];
      if (!id || !state) return { ok: false, reason: `No exact target for "${name}".` };

      const approvals = world.approvals[name];
      if (approvals) {
        const ack = await this.persistApprovals(name, approvals, baseMs, live?.task === name ? live.seq : null);
        if (!ack.ok) return { ok: false, reason: `${name}: ${ack.reason}` };
      }
      if (!isCurrent()) return { ok: false, reason: 'superseded' };

      let items = state.items as DemoItem[];
      if (approvals) {
        const mapped = await this.revisionItemsFor(name, approvals, items);
        if ('reason' in mapped) return { ok: false, reason: `${name}: ${mapped.reason}` };
        items = mapped.items;
      }
      if (!isCurrent()) return { ok: false, reason: 'superseded' };

      const wantsComplete = state.progress === 100;
      const writeProgress = wantsComplete ? 99 : state.progress;
      const { data, error } = await supabase.from('tasks').update({
        action_items: items as unknown as Json,
        timecoded_notes: state.notes as unknown as Json,
        media_versions: state.versions as unknown as Json,
        comments: state.comments as unknown as Json,
        media_url: state.mediaUrl,
        progress: writeProgress,
      }).eq('id', id).eq('space_id', this.targets.spaceId).select('id, progress, action_items').maybeSingle();
      if (error || !data) return { ok: false, reason: `${name}: not saved (${error?.message ?? 'no row returned'})` };
      const savedItems = Array.isArray(data.action_items) ? data.action_items.length : -1;
      if (savedItems !== items.length) return { ok: false, reason: `${name}: action items did not save.` };
      const isParent = this.scenario.tasks.some(t => t.parentName === name);
      if (!wantsComplete && !isParent && data.progress !== writeProgress) {
        return { ok: false, reason: `${name}: saved progress ${data.progress}%, expected ${writeProgress}%.` };
      }
      if (wantsComplete) {
        const { data: rpc, error: rpcErr } = await supabase.rpc('complete_task_if_ready', { _task_id: id });
        const result = rpc as { ok?: boolean; reason?: string } | null;
        if (rpcErr || result?.ok !== true) {
          return { ok: false, reason: `${name}: completion refused (${result?.reason ?? rpcErr?.message ?? 'unknown'})` };
        }
        const { data: after, error: afterErr } = await supabase.from('tasks').select('progress').eq('id', id).maybeSingle();
        if (afterErr || after?.progress !== 100) return { ok: false, reason: `${name}: completion not confirmed.` };
      }
    }
    return { ok: true };
  }

  /** Canonical revision items from the SAVED rejected review rows for this task. */
  private async revisionItemsFor(taskName: string, rows: DemoApprovalRow[], items: DemoItem[]) {
    const ids: string[] = [];
    for (const r of [...rows].sort((a, b) => a.seq - b.seq)) {
      if (r.status !== 'rejected') continue;
      const ref = this.rows.get(`${taskName}#${r.seq}`);
      if (!ref) return { ok: false as const, reason: 'rejected review has no saved record.' };
      ids.push(ref.id);
    }
    if (!ids.length) return canonicalRevisionItems(items, []);
    const { data, error } = await supabase.from('task_approvals').select('id, status, changes_needed').in('id', ids);
    if (error || !data) return { ok: false as const, reason: `could not read rejected reviews (${error?.message ?? 'no rows'}).` };
    const byId = new Map((data as Array<{ id: string; status: string; changes_needed: string[] | null }>).map(d => [d.id, d]));
    const rejected: Array<{ id: string; changes_needed: string[] | null }> = [];
    for (const id of ids) {
      const d = byId.get(id);
      if (!d || d.status !== 'rejected') return { ok: false as const, reason: 'a rejected review is not saved as rejected.' };
      rejected.push({ id, changes_needed: d.changes_needed });
    }
    return canonicalRevisionItems(items, rejected);
  }

  /** Read the saved status of the live checkpoint row. */
  async liveDecision(taskName: string, seq: number): Promise<{ ok: true; status: string } | { ok: false; reason: string }> {
    const ref = this.liveRowFor(taskName, seq);
    if (!ref) return { ok: false, reason: 'No live review row for this chapter.' };
    const { data, error } = await supabase.from('task_approvals').select('status').eq('id', ref.id).maybeSingle();
    if (error || !data) return { ok: false, reason: error?.message ?? 'Review row not found.' };
    ref.status = data.status;
    return { ok: true, status: data.status };
  }
}

/** Current-round pending row for a checkpoint reviewer in the pre-decision world. */
export function checkpointSeq(world: DemoWorld, task: string, slot: string): number | null {
  const rows = world.approvals[task];
  const mine = rows && currentRound(rows).find(r => r.approverSlot === slot);
  return mine && mine.status === 'pending' ? mine.seq : null;
}

// ─── Owned synthetic review ledger ───
//
// Review-link columns (token_*) are backend-only: client roles hold no SELECT
// on them, so the walkthrough never reads, filters or selects them. Instead,
// every synthetic review id is generated here and recorded durably BEFORE the
// insert, so an ambiguous response or a page reload can only ever touch rows
// this walkthrough created. Anything else on the sample tasks is foreign.

const LEDGER_KEY = `workitect-demo-owned-reviews:${DEMOS_INSTANCE_ID}`;
export function loadOwnedReviewIds(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(LEDGER_KEY) ?? '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch { return []; }
}
function writeLedger(ids: string[]): boolean {
  try {
    const raw = JSON.stringify([...new Set(ids)].sort());
    localStorage.setItem(LEDGER_KEY, raw);
    return localStorage.getItem(LEDGER_KEY) === raw;
  } catch { return false; }
}
export const recordOwnedReviewId = (id: string) => writeLedger([...loadOwnedReviewIds(), id]);
const forgetOwnedReviewIds = (ids: string[]) => { const drop = new Set(ids); return writeLedger(loadOwnedReviewIds().filter(i => !drop.has(i))); };
export const isOwnedReviewId = (id: string) => loadOwnedReviewIds().includes(id);

/** Token-free columns the walkthrough reads (subset of TASK_APPROVAL_SAFE_COLUMNS). */
export const DEMO_REVIEW_COLUMNS = 'id, task_id, project_id, approver_type, approver_user_id, approver_email, status, scope, action_item_id, created_at' as const;

/**
 * Delete review rows on these tasks, but only if EVERY row is an owned
 * synthetic walkthrough row (ledger id + synthetic identity + fixed project +
 * task scope). Any other row refuses without deleting anything.
 */
export async function deleteSyntheticApprovals(taskIds: string[]): Promise<DemoAck> {
  const { data, error } = await supabase.from('task_approvals').select(DEMO_REVIEW_COLUMNS).in('task_id', taskIds);
  if (error || !data) return { ok: false, reason: `Could not read review records: ${error?.message ?? 'no data'}` };
  const owned = new Set(loadOwnedReviewIds());
  const foreign = data.filter(r => !owned.has(r.id) || !syntheticPersonaSlot(r) || r.project_id !== DEMOS_PROJECT_ID || r.scope !== 'task' || r.action_item_id);
  if (foreign.length) return { ok: false, reason: `${foreign.length} review record(s) on the sample tasks were not created by the walkthrough; nothing was removed.` };
  if (!data.length) return { ok: true };
  const ids = data.map(r => r.id);
  const { error: delErr } = await supabase.from('task_approvals').delete().in('id', ids).in('task_id', taskIds).eq('project_id', DEMOS_PROJECT_ID);
  if (delErr) return { ok: false, reason: `Could not remove walkthrough reviews: ${delErr.message}` };
  const { data: left, error: leftErr } = await supabase.from('task_approvals').select('id').in('id', ids);
  if (leftErr || !left) return { ok: false, reason: `Could not confirm review cleanup: ${leftErr?.message ?? 'no data'}` };
  if (left.length) return { ok: false, reason: 'Some walkthrough reviews could not be removed.' };
  forgetOwnedReviewIds(ids);
  return { ok: true };
}

// ─── Pre-walkthrough snapshot ───

const SNAP_TASK_FIELDS = ['progress', 'status', 'start_time', 'end_time', 'action_items', 'comments', 'timecoded_notes', 'media_versions', 'media_url'] as const;
/** Verified unchanged, never written. */
const SNAP_TASK_IDENTITY = ['name', 'parent_id', 'root_project_id', 'project_id', 'space_id', 'deleted_at'] as const;
const SNAP_TASK_COLS = `id, ${[...SNAP_TASK_FIELDS, ...SNAP_TASK_IDENTITY].join(', ')}`;

export interface ScenarioSnapshot {
  version: 2;
  scenarioId: string;
  instanceId: string;
  capturedAt: string;
  taskIds: string[];
  tasks: Array<Record<string, unknown> & { id: string; progress: number; status: string }>;
  /** Always empty: scenarios with pre-existing review records refuse to start. */
  approvals: [];
  /** Full dependency rows (every column), verified exactly on restore. */
  dependencies: Array<Record<string, unknown> & { id: string }>;
}

const STORE_KEY = (scenarioId: string) => `workitect-demo-restore:${DEMOS_INSTANCE_ID}:${scenarioId}`;

export function saveDurableSnapshot(s: ScenarioSnapshot): boolean {
  try {
    localStorage.setItem(STORE_KEY(s.scenarioId), JSON.stringify(s));
    return localStorage.getItem(STORE_KEY(s.scenarioId)) === JSON.stringify(s);
  } catch { return false; }
}
export function loadDurableSnapshot(scenarioId: string): ScenarioSnapshot | null {
  try {
    const raw = localStorage.getItem(STORE_KEY(scenarioId));
    if (!raw) return null;
    const s = JSON.parse(raw) as ScenarioSnapshot;
    return s?.version === 2 && s.instanceId === DEMOS_INSTANCE_ID && Array.isArray(s.taskIds) && Array.isArray(s.approvals) && s.approvals.length === 0 ? s : null;
  } catch { return null; }
}
/** Old-format material (v1) cannot be restored exactly; report it instead of guessing. */
export function hasUnrestorableSnapshot(scenarioId: string): boolean {
  try {
    const raw = localStorage.getItem(STORE_KEY(scenarioId));
    return !!raw && loadDurableSnapshot(scenarioId) === null;
  } catch { return false; }
}
export function clearDurableSnapshot(scenarioId: string) {
  try { localStorage.removeItem(STORE_KEY(scenarioId)); } catch { /* storage unavailable */ }
}

async function readDeps(ids: string[]) {
  return supabase.from('dependencies').select('*').or(
    `from_task_id.in.(${ids.join(',')}),to_task_id.in.(${ids.join(',')})`,
  ).order('id', { ascending: true });
}

export async function captureScenarioSnapshot(
  scenario: DemoScenario, targets: ScenarioTargets,
): Promise<{ ok: true; snapshot: ScenarioSnapshot } | { ok: false; reason: string }> {
  const ids = Object.values(targets.taskIds);
  const [t, a, d] = await Promise.all([
    supabase.from('tasks').select(SNAP_TASK_COLS).in('id', ids),
    supabase.from('task_approvals').select('id').in('task_id', ids),
    readDeps(ids),
  ]);
  if (t.error || a.error || d.error || !t.data || !a.data || !d.data) {
    return { ok: false, reason: `Could not snapshot the sample project: ${t.error?.message ?? a.error?.message ?? d.error?.message ?? 'no data'}` };
  }
  if (t.data.length !== ids.length) return { ok: false, reason: 'Snapshot incomplete — not every scenario task was readable.' };
  if (a.data.length > 0) {
    return { ok: false, reason: `The sample tasks already have ${a.data.length} review record(s). The walkthrough cannot restore those exactly, so it did not start and changed nothing.` };
  }
  return {
    ok: true,
    snapshot: {
      version: 2, scenarioId: scenario.id, instanceId: DEMOS_INSTANCE_ID, capturedAt: new Date().toISOString(), taskIds: ids,
      tasks: t.data as unknown as ScenarioSnapshot['tasks'],
      approvals: [],
      dependencies: d.data as unknown as ScenarioSnapshot['dependencies'],
    },
  };
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const sameTime = (a: unknown, b: unknown) =>
  a == null || b == null ? a == b : new Date(a as string).getTime() === new Date(b as string).getTime();
const canon = (rows: Array<Record<string, unknown>>) =>
  JSON.stringify([...rows].map(r => Object.keys(r).sort().map(k => [k, r[k]])).sort((x, y) => (JSON.stringify(x) < JSON.stringify(y) ? -1 : 1)));

/**
 * Restore exactly the captured rows. Only fields that differ are written;
 * dates are not written unless they changed. Walkthrough review rows (the
 * only kind allowed) are removed; identity columns and full dependency rows
 * are verified unchanged.
 */
export async function restoreScenarioSnapshot(snapshot: ScenarioSnapshot, isCurrent: () => boolean = () => true): Promise<DemoAck> {
  const ids = snapshot.taskIds;
  const { data: current, error: curErr } = await supabase.from('tasks').select(SNAP_TASK_COLS).in('id', ids);
  if (curErr || !current) return { ok: false, reason: `Could not read the sample project: ${curErr?.message ?? 'no data'}` };
  if (current.length !== ids.length) return { ok: false, reason: 'Some sample tasks are no longer readable; restore stopped.' };
  const byId = new Map((current as unknown as Array<Record<string, unknown>>).map(r => [r.id as string, r]));
  for (const t of snapshot.tasks) {
    const cur = byId.get(t.id);
    if (!cur) return { ok: false, reason: 'A sample task is missing; restore stopped.' };
    for (const f of SNAP_TASK_IDENTITY) {
      if (f in t && !(f === 'deleted_at' ? sameTime(cur[f], t[f]) : same(cur[f], t[f]))) {
        return { ok: false, reason: `A sample task's ${f} changed outside the walkthrough; restore stopped without writing.` };
      }
    }
  }

  const del = await deleteSyntheticApprovals(ids);
  if (!del.ok) return del;

  for (const t of snapshot.tasks) {
    if (!isCurrent()) return { ok: false, reason: 'superseded' };
    const cur = byId.get(t.id)!;
    const patch: Record<string, unknown> = {};
    for (const f of SNAP_TASK_FIELDS) {
      if (!(f in t)) continue;
      const differs = f === 'start_time' || f === 'end_time' ? !sameTime(cur[f], t[f]) : !same(cur[f], t[f]);
      if (differs) patch[f] = t[f];
    }
    if (!Object.keys(patch).length) continue;
    const { data, error } = await supabase.from('tasks').update(patch as never).eq('id', t.id).select('progress, status').maybeSingle();
    if (error || !data) return { ok: false, reason: `Could not restore a sample task: ${error?.message ?? 'no row returned'}` };
    if ('progress' in patch && data.progress !== t.progress) return { ok: false, reason: 'A restored task did not keep its original progress.' };
  }

  const [appr, deps] = await Promise.all([
    supabase.from('task_approvals').select('id').in('task_id', ids),
    readDeps(ids),
  ]);
  if (appr.error || deps.error || !appr.data || !deps.data) return { ok: false, reason: 'Could not verify the restore.' };
  if (appr.data.length !== 0) return { ok: false, reason: 'Review records remain on the sample tasks after restore.' };
  if (canon(deps.data as unknown as Array<Record<string, unknown>>) !== canon(snapshot.dependencies)) {
    return { ok: false, reason: 'Task links differ from the snapshot.' };
  }
  return { ok: true };
}

// ─── First-time scenario creation (exact, fixed target, owned manifest) ───

const DEMOS_PROJECT_ID = '213ec846-5e2c-420b-871b-2dce09aa4a53';
const DEMOS_SPACE_ID = '3b68785e-254e-458a-9e9b-9f4093d929c9';
const HOUR_MS = 3_600_000;

/** Deterministic sibling-sequential schedule: every child inside its parent, FS links satisfied. */
export function planScenarioSchedule(scenario: DemoScenario, startMs: number): Map<string, { start: number; end: number }> {
  const kids = new Map<string, string[]>();
  for (const t of scenario.tasks) if (t.parentName) kids.set(t.parentName, [...(kids.get(t.parentName) ?? []), t.name]);
  const byName = new Map(scenario.tasks.map(t => [t.name, t]));
  const span = new Map<string, number>();
  const measure = (n: string): number => {
    const own = (byName.get(n)?.durationHours ?? 1) * HOUR_MS;
    const sum = (kids.get(n) ?? []).reduce((a, k) => a + measure(k), 0);
    const v = Math.max(own, sum);
    span.set(n, v);
    return v;
  };
  const out = new Map<string, { start: number; end: number }>();
  const place = (n: string, at: number) => {
    out.set(n, { start: at, end: at + span.get(n)! });
    let cur = at;
    for (const k of kids.get(n) ?? []) { place(k, cur); cur += span.get(k)!; }
  };
  let cursor = startMs;
  for (const root of scenario.tasks.filter(t => !t.parentName)) { measure(root.name); place(root.name, cursor); cursor += span.get(root.name)!; }
  return out;
}

async function rollbackCreated(taskIds: string[], depIds: string[]): Promise<string | null> {
  if (depIds.length) {
    const { error } = await supabase.from('dependencies').delete().in('id', depIds);
    if (error) return `links: ${error.message}`;
  }
  // Children first (reverse creation order).
  for (const id of [...taskIds].reverse()) {
    const { error } = await supabase.from('tasks').delete().eq('id', id).eq('project_id', DEMOS_PROJECT_ID).eq('space_id', DEMOS_SPACE_ID);
    if (error) return `task ${id}: ${error.message}`;
  }
  if (taskIds.length) {
    const { data, error } = await supabase.from('tasks').select('id').in('id', taskIds);
    if (error || !data) return 'could not confirm cleanup';
    if (data.length) return `${data.length} created task(s) remain`;
  }
  return null;
}

/**
 * Create a missing scenario once, in the fixed Demos project and space only.
 * Every insert is verified; any failure or cancellation removes exactly the
 * rows this call created (never anything else). Refuses if any live root of
 * the scenario's name already exists (no duplicates on retry).
 */
export async function createScenarioExact(
  scenario: DemoScenario, instanceId: string | null | undefined, signal: AbortSignal, startMs = Date.now(),
): Promise<DemoAck> {
  if (instanceId !== DEMOS_INSTANCE_ID) return { ok: false, reason: 'Walkthroughs only run in the Demos workspace.' };
  const roots = scenario.tasks.filter(t => !t.parentName);
  if (roots.length !== 1) return { ok: false, reason: 'Scenario must have exactly one root.' };
  const rootName = roots[0].name;
  const { data: existing, error: exErr } = await supabase.from('tasks').select('id')
    .eq('space_id', DEMOS_SPACE_ID).eq('name', rootName).is('parent_id', null).is('deleted_at', null);
  if (exErr || !existing) return { ok: false, reason: `Could not check for the sample project: ${exErr?.message ?? 'no data'}` };
  if (existing.length) return { ok: false, reason: `"${rootName}" already exists; nothing was created.` };

  const times = planScenarioSchedule(scenario, Math.floor(startMs / HOUR_MS) * HOUR_MS);
  const ids = new Map(scenario.tasks.map(t => [t.name, crypto.randomUUID()]));
  const rootId = ids.get(rootName)!;
  const created: string[] = [];
  const createdDeps: string[] = [];
  const abortWith = async (reason: string): Promise<DemoAck> => {
    const rb = await rollbackCreated(created, createdDeps);
    return { ok: false, reason: rb ? `${reason} Cleanup incomplete (${rb}); the next start will refuse until checked.` : `${reason} Nothing was left behind.` };
  };

  // Parents before children (script order already lists parents first; enforce it).
  const ordered: typeof scenario.tasks = [];
  const visit = (name: string) => {
    const t = scenario.tasks.find(x => x.name === name)!;
    ordered.push(t);
    for (const c of scenario.tasks.filter(x => x.parentName === name)) visit(c.name);
  };
  visit(rootName);
  if (ordered.length !== scenario.tasks.length) return { ok: false, reason: 'Scenario tasks are not a single tree.' };

  for (const t of ordered) {
    if (signal.aborted) return abortWith('Cancelled.');
    const id = ids.get(t.name)!;
    const w = times.get(t.name)!;
    const items = (t.checklist ?? []).map((text, i) => ({ id: `ai-${id}-${i}`, text, completed: false }));
    const row = {
      id, project_id: DEMOS_PROJECT_ID, space_id: DEMOS_SPACE_ID, name: t.name,
      parent_id: t.parentName ? ids.get(t.parentName)! : null, root_project_id: rootId,
      intent: t.intent ?? '', specs: t.specs ?? [], action_items: items,
      start_time: new Date(w.start).toISOString(), end_time: new Date(w.end).toISOString(),
      progress: 0, status: 'active', assignee: t.assigneeSlot, folder: scenario.name,
      schedule_mode: t.scheduleMode ?? scenario.scheduleMode ?? 'hourly',
      media_url: t.mediaUrl ?? null,
      media_versions: t.mediaUrl ? [{ version: 1, url: t.mediaUrl, notes: [], createdAt: new Date(startMs).toISOString(), ...(t.mediaTotalPages ? { totalPages: t.mediaTotalPages } : {}) }] : [],
    };
    const { data, error } = await supabase.from('tasks').insert(row as never).select('id, name, parent_id, project_id, space_id, root_project_id').maybeSingle();
    if (error || !data) return abortWith(`"${t.name}" was not created (${error?.message ?? 'no row returned'}).`);
    created.push(id);
    if (data.id !== id || data.name !== t.name || data.project_id !== DEMOS_PROJECT_ID || data.space_id !== DEMOS_SPACE_ID
      || data.parent_id !== row.parent_id || data.root_project_id !== rootId) {
      return abortWith(`"${t.name}" was saved differently than requested.`);
    }
  }
  for (const dep of scenario.dependencies ?? []) {
    if (signal.aborted) return abortWith('Cancelled.');
    const from = ids.get(dep.from); const to = ids.get(dep.to);
    if (!from || !to) return abortWith(`Link ${dep.from} → ${dep.to} names an unknown task.`);
    const { data, error } = await supabase.from('dependencies')
      .insert({ project_id: DEMOS_PROJECT_ID, from_task_id: from, to_task_id: to, type: dep.type, lag_minutes: 0 } as never)
      .select('id, from_task_id, to_task_id, type').maybeSingle();
    if (error || !data) return abortWith(`Link ${dep.from} → ${dep.to} was not created (${error?.message ?? 'no row returned'}).`);
    createdDeps.push(data.id);
    if (data.from_task_id !== from || data.to_task_id !== to || data.type !== dep.type) return abortWith(`Link ${dep.from} → ${dep.to} was saved differently.`);
  }
  if (signal.aborted) return abortWith('Cancelled.');
  return { ok: true };
}
